import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { AuthContext } from "../../../contexts/AuthContext";
import { toast } from "react-toastify";
import { userService } from "../../../services/api/authenService";
import { notiService } from "../../../services/api/notiService";
import { useSidebarLayout } from "../../../contexts/SidebarLayoutContext";

const useIsTabVisible = () => {
  const [visible, setVisible] = useState(() => !document.hidden);
  useEffect(() => {
    const onVis = () => setVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);
  return visible;
};

const Header = () => {
  const { token_client, user_client, dispatch } = useContext(AuthContext);
  const { isSidebarOpen, toggleSidebar, hasSidebar } = useSidebarLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const shouldReduceMotion = useReducedMotion();
  const isTabVisible = useIsTabVisible();

  const [title, setTitle] = useState("");
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isUserInfoOpen, setIsUserInfoOpen] = useState(false);
  const [noti, setNoti] = useState([]);
  const [notiNum, setNotiNum] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingFirstNoti, setLoadingFirstNoti] = useState(false);
  const pageRef = useRef(1);

  const notiBtnRef = useRef(null);
  const userBtnRef = useRef(null);
  const notiPanelRef = useRef(null);
  const userPanelRef = useRef(null);

  const toggleNotification = () => {
    setIsNotificationOpen((prev) => !prev);
    setIsUserInfoOpen(false);
  };
  const toggleUserInfo = () => {
    setIsUserInfoOpen((prev) => !prev);
    setIsNotificationOpen(false);
  };

  const formatNoti = (data) =>
    data.map((item) => ({
      ...item,
      icon: "/images/notification-icon.png",
      note: item.message?.replace?.(item.profileId, "") ?? item.message ?? "",
      timeText: item.createdAt
        ? (() => {
            const d = new Date(item.createdAt);
            const diff = Date.now() - d.getTime();
            const mins = Math.floor(diff / 60000);
            if (mins < 1) return "Vừa xong";
            if (mins < 60) return `${mins} phút trước`;
            if (mins < 1440) return `${Math.floor(mins / 60)} giờ trước`;
            return d.toLocaleDateString("vi-VN");
          })()
        : "",
    }));

  const fetchNoti = useCallback(
    async (reset = false) => {
      try {
        if (reset) setLoadingFirstNoti(true);

        const nextPage = reset ? 1 : pageRef.current;
        const res = await notiService.get_all(token_client, nextPage);
        const formatted = formatNoti(res.data || []);

        if (reset) {
          setNoti(formatted);
          pageRef.current = 2;
          setNotiNum(0);
        } else {
          setNoti((prev) => [...prev, ...formatted]);
          pageRef.current += 1;
        }

        setHasMore(res.pagination?.currentPage < res.pagination?.totalPages);
      } catch (err) {
        toast.dismiss();
        toast.error("Lỗi khi tải thông báo");
      } finally {
        if (reset) setLoadingFirstNoti(false);
      }
    },
    [token_client],
  );

  const fetchNotiCount = useCallback(async () => {
    try {
      const res = await notiService.get_count(token_client);
      setNotiNum(res?.newNotiCount ?? 0);
    } catch {
      // silent
    }
  }, [token_client]);

  useEffect(() => {
    fetchNotiCount();
    if (!isTabVisible) return;
    const interval = setInterval(fetchNotiCount, 30_000);
    return () => clearInterval(interval);
  }, [fetchNotiCount, isTabVisible]);

  useEffect(() => {
    if (isNotificationOpen) fetchNoti(true);
  }, [isNotificationOpen, fetchNoti]);

  /** ========= Title resolver + animation ========= */
  useEffect(() => {
    const path = location.pathname;
    const regexTitles = [
      {
        regex: /\/danh-sach-dich-vu\/dich-vu\/form\d+$/,
        title: "Đăng ký dịch vụ",
      },
      { regex: /\/quan-ly-ho-so\/ho-so\/form\d+$/, title: "Hồ sơ" },
      {
        regex: /\/tai-khoan-nhan-vien\/nhan-vien\/[\w-]+\/chinh-sua$/,
        title: "Chỉnh sửa tài khoản nhân viên",
      },
      {
        regex: /\/tai-khoan-nhan-vien\/nhan-vien\/[\w-]+$/,
        title: "Tài khoản nhân viên",
      },
      {
        regex: /\/huong-dan-thu-tuc\/thu-tuc\/[\w-]+$/,
        title: "Chi tiết thủ tục",
      },
      { regex: /\/tai-lieu\/bai-viet\/[\w-]+$/, title: "Chi tiết bài viết" },
      {
        regex: /\/danh-sach-yeu-cau\/ticket\/[\w-]+$/,
        title: "Thông tin ticket",
      },
      { regex: /\/quan-ly-ho-so\/ho-so\/[\w-]+$/, title: "Chi tiết hồ sơ" },
      {
        regex: /\/quan-ly-ho-so\/ho-so\/[\w-]+\/chinh-sua$/,
        title: "Chỉnh sửa hồ sơ",
      },
      {
        regex: /\/danh-sach-dich-vu\/dich-vu\/[\w-]+$/,
        title: "Đăng ký dịch vụ",
      },
    ];
    const fallbackMap = {
      "/tai-khoan-cua-toi/chinh-sua": "Chỉnh sửa tài khoản",
      "/mua-ban-so-huu-tri-tue": "Mua bán sở hữu trí tuệ",
      "/danh-sach-dich-vu": "Dịch vụ",
      "/danh-sach-hop-dong": "Danh sách hợp đồng / chứng từ",
      "/quan-ly-ho-so": "Quản lý hồ sơ",
      "/danh-muc-tai-san-tri-tue": "Danh mục tài sản trí tuệ",
      "/huong-dan-thu-tuc": "Hướng dẫn thủ tục",
      "/tai-lieu": "Tài liệu",
      "/tai-khoan-cua-toi": "Tài khoản của tôi",
      "/tai-khoan-nhan-vien": "Tài khoản nhân viên",
      "/tai-khoan-nhan-vien/tao-nhan-vien-moi": "Tạo tài khoản nhân viên",
      "/danh-sach-yeu-cau/them-moi": "Tạo ticket mới",
      "/danh-sach-yeu-cau": "Quản lý tickets",
      "/bao-phi": "Báo phí",
      "/bao-cao-thong-ke/tong-quan": "Báo cáo thống kê",
      "/bao-cao-thong-ke/dich-vu": "Báo cáo dịch vụ",
      "/bao-cao-thong-ke/ho-so": "Báo cáo hồ sơ",
      "/bao-cao-thong-ke/tai-chinh": "Báo cáo tài chính",
    };
    const matched = regexTitles.find((r) => r.regex.test(path));
    const resolved = matched?.title || fallbackMap[path] || "Dịch vụ";
    setTitle((prev) => (prev !== resolved ? resolved : prev));
  }, [location.pathname]);

  /** ========= Escape key + click outside ========= */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setIsNotificationOpen(false);
        setIsUserInfoOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      const target = e.target;

      if (
        isNotificationOpen &&
        notiPanelRef.current &&
        !notiPanelRef.current.contains(target) &&
        !notiBtnRef.current?.contains(target)
      ) {
        setIsNotificationOpen(false);
      }

      if (
        isUserInfoOpen &&
        userPanelRef.current &&
        !userPanelRef.current.contains(target) &&
        !userBtnRef.current?.contains(target)
      ) {
        setIsUserInfoOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [isNotificationOpen, isUserInfoOpen]);

  const badgeVariants = {
    hidden: { scale: 0.6, opacity: 0 },
    show: {
      scale: [0.8, 1.2, 1],
      opacity: 1,
      transition: { duration: 0.36, ease: [0.22, 1, 0.36, 1] },
    },
    pulse: shouldReduceMotion
      ? {}
      : {
          scale: [1, 1.1, 1],
          transition: { duration: 1.5, repeat: Infinity, ease: "easeInOut" },
        },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: 6 },
    show: { opacity: 1, y: 0, transition: { duration: 0.22 } },
    exit: { opacity: 0, y: -6, transition: { duration: 0.18 } },
  };

  const handleLogout = async () => {
    try {
      await userService.log_out();
      dispatch({ type: "LOGOUT" });
      toast.success("Đăng xuất thành công!");
      navigate("/login");
    } catch {
      toast.dismiss();
      toast.error("Đăng xuất không thành công!");
    }
  };

  const userDisplayName =
    user_client?.fullName ||
    user_client?.email ||
    user_client?.username ||
    "Tài khoản";
  const userRoleLabel =
    user_client?.role === "Staff"
      ? "Nhân viên"
      : user_client?.role === "Manager"
        ? "Quản lý"
        : "Cộng tác viên";

  return (
    <header className="relative z-30 flex w-full flex-col border-b border-white/10 bg-[#0155F8] shadow-[0_10px_28px_rgba(1,85,248,0.22)]">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-10 -top-8 h-20 w-20 rounded-full bg-white/10" />
        <div className="absolute left-[44%] top-4 size-5 rounded-full border-[5px] border-white/10" />
        <div className="absolute right-[18%] -top-24 size-48 rounded-full border-[32px] border-white/10" />
        <div className="absolute -bottom-14 left-[72%] h-28 w-28 rounded-full border-[24px] border-white/10" />
      </div>

      <div className="relative z-10 flex min-h-[64px] w-full items-center justify-between gap-4 px-6 py-2">
        <div className="flex min-w-0 items-center gap-3">
          <Link
            to="/"
            className="group inline-flex h-11 shrink-0 items-center gap-2 rounded-xl px-2 text-white transition-colors hover:bg-white/10"
          >
            <img
              src="/logos/logo_vector.svg"
              alt="logo"
              className="size-8 shrink-0 brightness-0 invert"
            />
            <span className="text-xl font-sf-semibold text-white transition-colors group-hover:text-white/90">
              Sohuutritue.vn
            </span>
          </Link>

          {hasSidebar && (
            <button
              onClick={toggleSidebar}
              className="inline-flex size-9 shrink-0 items-center justify-center rounded-xl border border-transparent text-white transition-colors hover:border-white/20 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
              aria-label={isSidebarOpen ? "Thu gọn sidebar" : "Mở rộng sidebar"}
              title={isSidebarOpen ? "Thu gọn" : "Mở rộng"}
            >
              <img
                alt=""
                src="/icons/arrow-left-double-sharp.svg"
                className={`size-3.5 brightness-0 invert transition-transform duration-300 ${
                  isSidebarOpen ? "rotate-180" : "rotate-0"
                }`}
              />
            </button>
          )}

          <div className="h-7 w-px shrink-0 bg-white/25" />

          <div className="relative min-w-0">
            <AnimatePresence mode="wait">
              <motion.h1
                key={title}
                variants={titleVariants}
                initial="hidden"
                animate="show"
                exit="exit"
                className="block max-w-[380px] truncate rounded-full px-3 py-2 text-sm font-sf-medium leading-none text-white shadow-sm shadow-blue-950/10 backdrop-blur"
              >
                {title}
              </motion.h1>
            </AnimatePresence>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2 lg:gap-3">
          <div className="hidden items-center gap-2 text-sm font-medium text-white xl:flex">
            <a
              href="tel:0819009697"
              className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-xl border border-white/20 bg-white/10 px-3 shadow-sm shadow-blue-950/10 underline-offset-4 transition-colors hover:border-white/35 hover:bg-white/15 hover:underline"
            >
              <img
                src="/icons/phone-blue.svg"
                alt=""
                className="h-3.5 w-3.5 brightness-0 invert"
              />
              <span>08.1900.9697</span>
            </a>
            <a
              href="mailto:dangky@sohuutritue.vn"
              className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-xl border border-white/20 bg-white/10 px-3 shadow-sm shadow-blue-950/10 underline-offset-4 transition-colors hover:border-white/35 hover:bg-white/15 hover:underline"
            >
              <img
                src="/icons/mail-blue.svg"
                alt=""
                className="h-3.5 w-3.5 brightness-0 invert"
              />
              <span>dangky@sohuutritue.vn</span>
            </a>
          </div>

          <div className="relative">
            <button
              ref={notiBtnRef}
              onClick={toggleNotification}
              className="relative inline-flex size-10 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white shadow-sm shadow-blue-950/10 transition-colors hover:border-white/35 hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
              aria-haspopup="dialog"
              aria-expanded={isNotificationOpen}
              aria-controls="notification-popup"
              aria-label="Thông báo"
            >
              <img
                src="/icons/notification.svg"
                alt=""
                className="size-3.5 shrink-0 brightness-0 invert"
              />
              <AnimatePresence>
                {notiNum > 0 && (
                  <motion.span
                    variants={badgeVariants}
                    initial="hidden"
                    animate={["show", "pulse"]}
                    exit={{ opacity: 0, scale: 0.6 }}
                    className="absolute -right-1.5 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full border border-white bg-red-600 px-1 text-[10px] font-medium leading-none text-white"
                  >
                    {notiNum > 99 ? "99+" : notiNum}
                  </motion.span>
                )}
              </AnimatePresence>
            </button>

            {isNotificationOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setIsNotificationOpen(false)}
                />

                <div
                  id="notification-popup"
                  ref={notiPanelRef}
                  className="absolute right-0 top-[calc(100%+8px)] z-[60] flex max-h-[520px] w-[420px] max-w-[90vw] flex-col overflow-hidden rounded-xl border border-slate-100 bg-white shadow-[0_16px_40px_rgba(15,23,42,0.14)]"
                >
                  <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-900">
                    Thông báo
                  </div>

                  {loadingFirstNoti ? (
                    <div className="p-5 text-center text-sm text-slate-500">
                      Đang tải…
                    </div>
                  ) : (
                    <InfiniteScroll
                      dataLength={noti.length}
                      next={() => fetchNoti(false)}
                      hasMore={hasMore}
                      loader={
                        <p className="py-3 text-center text-sm text-gray-500">
                          Đang tải thêm…
                        </p>
                      }
                      endMessage={
                        <p className="py-3 text-center text-xs text-gray-400">
                          Không còn thông báo
                        </p>
                      }
                      height={440}
                    >
                      <ul>
                        {noti.map((n) => (
                          <li
                            key={n._id}
                            className={`flex cursor-pointer items-start gap-3 border-b border-slate-100 px-4 py-3 transition-colors ${
                              n.status === "Read"
                                ? "bg-white hover:bg-gray-50"
                                : "bg-blue-50 hover:bg-blue-100"
                            }`}
                            onClick={() => {
                              setIsNotificationOpen(false);
                              navigate(`/quan-ly-ho-so/ho-so/${n.profileId}`);
                            }}
                          >
                            <img
                              src={n.icon}
                              alt=""
                              className="h-8 w-8 shrink-0"
                            />
                            <div className="min-w-0">
                              <p className="text-sm leading-5 line-clamp-2">
                                {n.note}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {n.timeText || "—"}
                              </p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </InfiniteScroll>
                  )}
                </div>
              </>
            )}
          </div>

          <button
            ref={userBtnRef}
            onClick={toggleUserInfo}
            className="group flex h-12 w-auto max-w-[320px] cursor-pointer items-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-2.5 py-1.5 text-white shadow-sm shadow-blue-950/10 backdrop-blur transition-colors hover:border-white/35 hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
            aria-haspopup="menu"
            aria-expanded={isUserInfoOpen}
            aria-controls="user-menu"
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <div className="min-w-0 text-left">
                <p
                  className="max-w-[180px] truncate text-base font-sf-semibold leading-5 text-white"
                  title={userDisplayName}
                >
                  {userDisplayName}
                </p>
                <p className="text-xs font-be-vietnam-light leading-4 text-white/75">
                  {userRoleLabel}
                </p>
              </div>
              <img
                src={
                  user_client?.avatar ||
                  user_client?.avatarUrl ||
                  "/images/avatar-default.png"
                }
                alt="Avatar"
                className="size-8 shrink-0 rounded-full object-cover object-center ring-2 ring-white/30"
              />
            </div>
            <img
              src="/icons/arrow-down.svg"
              alt=""
              className={`size-5 brightness-0 invert opacity-80 transition-transform duration-300 group-hover:opacity-100 ${
                isUserInfoOpen ? "rotate-180" : "rotate-0"
              }`}
            />
          </button>
        </div>
      </div>

      <div
        className={`hidden ${
          location.pathname === "/danh-sach-dich-vu"
            ? "justify-end"
            : "justify-between"
        }`}
      >
        {location.pathname !== "/danh-sach-dich-vu" && (
          <p className="text-sm md:text-base">Nền tảng số Sở hữu trí tuệ</p>
        )}
        <div className="flex items-center gap-6 text-sm md:gap-10">
          <div className="flex items-center gap-2">
            <img src="/icons/phone-blue.svg" alt="" aria-hidden />
            <a
              href="tel:0819009697"
              className="underline-offset-4 hover:underline"
            >
              08.1900.9697
            </a>
          </div>
          <div className="hidden items-center gap-2 sm:flex">
            <img src="/icons/mail-blue.svg" alt="" aria-hidden />
            <a
              href="mailto:dangky@sohuutritue.vn"
              className="underline-offset-4 hover:underline"
            >
              dangky@sohuutritue.vn
            </a>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isUserInfoOpen && (
          <>
            <motion.div
              key="user-overlay"
              className="fixed inset-0 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { duration: 0.15 } }}
              exit={{ opacity: 0, transition: { duration: 0.12 } }}
              onClick={() => setIsUserInfoOpen(false)}
            />
            <motion.div
              key="user-menu"
              id="user-menu"
              ref={userPanelRef}
              role="menu"
              className="absolute right-4 top-[calc(100%+8px)] z-[60] w-[232px] origin-top-right overflow-hidden rounded-xl border border-slate-100 bg-white shadow-[0_16px_40px_rgba(15,23,42,0.14)]"
              initial={{ opacity: 0, scale: 0.96, y: -4 }}
              animate={{
                opacity: 1,
                scale: 1,
                y: 0,
                transition: { duration: 0.16 },
              }}
              exit={{
                opacity: 0,
                scale: 0.98,
                y: -4,
                transition: { duration: 0.12 },
              }}
            >
              <Link
                to="/tai-khoan-cua-toi"
                className="block w-full px-4 py-3 text-left text-sm font-medium text-[#056BDA] transition-colors hover:bg-blue-50"
                role="menuitem"
                onClick={() => setIsUserInfoOpen(false)}
              >
                Xem thông tin tài khoản
              </Link>
              <button
                onClick={handleLogout}
                className="block w-full px-4 py-3 text-left text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                role="menuitem"
              >
                Đăng xuất
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
